import { makeObservable, action, observable } from 'mobx';
import { BaseService } from '../base';
import { OutstandingListModel } from './model';
import Outstanding from './interface';
import { FAST_ESCROW_ORDER_TYPE } from '@/stores/order/order.interface';

/** Cache：单个列表(Tab / 搜索结果)的分页状态 */
export interface IOutstandingListState {
  loading: boolean;
  hasMore: boolean;
  page_index: number;
  list: OutstandingListModel[];
}

const createListState = (): IOutstandingListState => ({
  loading: false,
  hasMore: true,
  page_index: 0,
  list: [],
});

export class CommonService extends BaseService {
  /** ******** 非 Cache(现状)：单列表 ******** */
  @observable loading: boolean = false;
  @observable hasMore: boolean = true;
  @observable dataList: OutstandingListModel[] = [];

  /** ******** Cache：双 Tab 各自独立分页 ******** */
  // Fulfilled to Buyer(order_type=0：Loan#2 + 非Cache)
  @observable fulfilledState: IOutstandingListState = createListState();
  // Advanced Shopee Fulfilled(order_type=1：Cache Loan#1)
  @observable advancedState: IOutstandingListState = createListState();

  /** ******** Cache：搜索 ******** */
  @observable searchKeyword: string = '';
  @observable hasSearched: boolean = false;
  @observable searchState: IOutstandingListState = createListState();

  protected page_index = 0;
  protected page_size = 10;

  constructor() {
    super();
    makeObservable(this);
  }

  @action
  initInfo() {
    this.loading = false;
    this.hasMore = true;
    this.page_index = 0;
    this.page_size = 10;
    this.dataList = [];
  }

  @action
  async fetchList() {
    // 如果已经标记当前状态为hasmore：false
    if (!this.hasMore || this.loading) return;
    this.loading = true;

    const { code, result } = await this.post(
      Outstanding.RefactorAPI.get_user_outstanding_repayments,
      {
        body: {
          status: 4, // 0. All 1. Pending 2. Overdue 3. Completed 4. Outstanding(for PC)
          pagination: {
            page_index: this.page_index + 1,
            page_size: this.page_size,
            cursor:
              this.dataList.length > 0
                ? this.dataList?.[this.dataList.length - 1]?.plan_id ?? '0'
                : '0',
          },
        },
      },
    );
    if (code === 0) {
      const { page_info, list = [] } = result;
      this.dataList = list ?? [];
      this.hasMore = page_info.has_more;
      this.page_index = page_info.page_index;
      this.loading = false;
      return result;
    }
    this.loading = false;
    this.hasMore = false;
  }

  /** ************** Cache 双 Tab START ************** */

  private getTabState(orderType: FAST_ESCROW_ORDER_TYPE): IOutstandingListState {
    return orderType === FAST_ESCROW_ORDER_TYPE.CACHE ? this.advancedState : this.fulfilledState;
  }

  @action
  private setTabState(orderType: FAST_ESCROW_ORDER_TYPE, patch: Partial<IOutstandingListState>) {
    if (orderType === FAST_ESCROW_ORDER_TYPE.CACHE) {
      this.advancedState = { ...this.advancedState, ...patch };
    } else {
      this.fulfilledState = { ...this.fulfilledState, ...patch };
    }
  }

  /**
   * Cache：按 order_type 拉取 Outstanding 列表(BFF 6.7 query_repayment_list)
   * 与非 Cache 同一接口，仅多传 order_type；两个 Tab 各自累加分页
   */
  @action
  async fetchTabList(orderType: FAST_ESCROW_ORDER_TYPE) {
    const state = this.getTabState(orderType);
    if (!state.hasMore || state.loading) return;
    this.setTabState(orderType, { loading: true });

    const { code, result } = await this.post(
      Outstanding.RefactorAPI.get_user_outstanding_repayments,
      {
        body: {
          status: 4,
          order_type: orderType,
          pagination: {
            page_index: state.page_index + 1,
            page_size: this.page_size,
            cursor: state.list.length > 0 ? state.list[state.list.length - 1]?.plan_id ?? '0' : '0',
          },
        },
      },
    );

    if (code === 0) {
      const { page_info, list = [] } = result;
      const cur = this.getTabState(orderType);
      this.setTabState(orderType, {
        list: [...cur.list, ...(list ?? [])],
        hasMore: page_info?.has_more,
        page_index: page_info?.page_index,
        loading: false,
      });
      return result;
    }
    this.setTabState(orderType, { loading: false, hasMore: false });
  }

  /** ************** Cache 双 Tab END ************** */

  /** ************** Cache 搜索 START ************** */

  /**
   * Cache：按搜索框关键字查询(BFF 6.13 QueryRepaymentListBySearchBarId)
   * @param keyword Order ID / Booking ID(入参字段名沿用 BFF 的 order_sn，后端按关键词搜两类单)
   * 出参条目与 6.7 列表同构，直接累加
   */
  @action
  async searchByKeyword(keyword: string, isLoadMore = false) {
    const kw = (keyword ?? '').trim();
    if (!kw) {
      this.clearSearch();
      return;
    }

    if (!isLoadMore) {
      this.searchKeyword = kw;
      this.hasSearched = true;
      this.searchState = createListState();
    }

    const state = this.searchState;
    if (!state.hasMore || state.loading) return;
    this.searchState = { ...this.searchState, loading: true };

    const { code, result } = await this.post(Outstanding.RefactorAPI.search_repayment_list, {
      body: {
        order_sn: kw,
        pagination: {
          page_index: state.page_index + 1,
          page_size: this.page_size,
          cursor: state.list.length > 0 ? state.list[state.list.length - 1]?.plan_id ?? '0' : '0',
        },
      },
    });

    if (code === 0) {
      const { page_info, list = [] } = result ?? {};
      this.searchState = {
        ...this.searchState,
        list: [...this.searchState.list, ...(list ?? [])],
        hasMore: page_info?.has_more ?? false,
        page_index: page_info?.page_index ?? this.searchState.page_index,
        loading: false,
      };
      return result;
    }
    this.searchState = { ...this.searchState, loading: false, hasMore: false };
  }

  @action
  clearSearch() {
    this.searchKeyword = '';
    this.hasSearched = false;
    this.searchState = createListState();
  }

  /** ************** Cache 搜索 END ************** */

  /** Cache：初始化双 Tab + 搜索态 */
  @action
  initCacheInfo() {
    this.fulfilledState = createListState();
    this.advancedState = createListState();
    this.clearSearch();
  }
}
